/**
 * ============================================================
 * SELLING PLAN DYNAMIC LOADER  —  v2.0.0
 * ============================================================
 *
 * Uses Shopify's AJAX Product API: /products/{handle}.js
 * (PUBLIC, no auth). Must be .js NOT .json — only .js includes
 * selling_plan_groups and variants[].selling_plan_allocations[].
 *
 * ------------------------------------------------------------
 * MATCH PRIORITY (highest → lowest)
 * ------------------------------------------------------------
 *   1. EXPLICIT ID   metafield is a raw selling plan ID
 *                    e.g. "690379620515" or a gid:// URL.
 *                    Verified against the variant's allocations.
 *
 *   2. CODE TOKEN    metafield is a codeword that appears as a
 *                    WHOLE TOKEN in the plan name / option value /
 *                    description / group name.
 *                    e.g. metafield "M1D30" matches the plan
 *                    "1 Month Subscription at 30% Discount M1D30".
 *                    Token = run of [a-z0-9] between any punctuation
 *                    or whitespace, so "M1D3" will NOT match "M1D30".
 *
 *   3. INTERVAL EXACT  legacy behaviour: normalized metafield equals
 *                      a normalized plan name or option value.
 *                      "2 Months" == "2 month" == "2month".
 *
 *   4. INTERVAL PARTIAL  legacy substring behaviour. Kept for
 *                        backwards compatibility, but logs a warning
 *                        when more than one plan qualifies.
 *
 * ------------------------------------------------------------
 * CODEWORD RESERVATION  (migration safety)
 * ------------------------------------------------------------
 * A plan whose name contains a codeword is RESERVED: tiers 3 and 4
 * skip it. It can only be reached by its code or its raw ID.
 *
 * This is what lets you add discount tiers without touching the
 * metafields you already have. Given a product with:
 *
 *     Plan A  "1 Month Subscription"
 *     Plan B  "1 Month Subscription at 30% Discount M1D30"
 *
 * a legacy metafield of "1 month" describes both — B's name contains
 * "1month" just as A's does. Without reservation the winner is
 * whichever allocation Shopify lists first, which is not a contract
 * you want a discount riding on. With reservation, "1 month" can
 * only ever land on A, and B is reachable only via "M1D30".
 *
 * Corollary: a candidate that looks like a code NEVER falls through
 * to interval matching. A typo'd "M1D3" fails loudly instead of
 * substring-matching its way into "...M1D30".
 *
 * If every plan on a product is coded and a variant still has a
 * legacy metafield, reservation is waived (otherwise the variant
 * would resolve to nothing) and a warning is logged.
 *
 * What counts as a codeword is options.codePattern, default
 * /^[a-z]{1,4}\d+[a-z]{1,4}\d+$/i — M1D30, S2D50, M12D100. Widen it
 * from the Liquid snippet via sp_code_pattern if your scheme differs.
 *
 *   5. FALLBACKS     single allocation → use it.
 *                    no metafield at all → first allocation
 *                    (disable with options.fallbackToFirstPlan: false).
 *
 * IMPORTANT: within a tier, every allocation is evaluated before a
 * winner is picked. If a tier produces multiple hits the match is
 * AMBIGUOUS — the first is used and a warning is logged telling you
 * to add codewords. The old code returned the first partial hit even
 * when a later allocation was an exact match; that is fixed.
 *
 * ------------------------------------------------------------
 * FALLBACK CHAINS
 * ------------------------------------------------------------
 * A metafield may hold a priority list separated by | , or ;
 *   "M1D30|M1D20|1 Month"
 * Each candidate runs the full ladder above, in order, until one hits.
 * Useful when rolling a promo out: point at the promo code first and
 * let it degrade to the evergreen plan if the promo isn't live.
 *
 * ------------------------------------------------------------
 * NAMING YOUR PLANS
 * ------------------------------------------------------------
 * Codes must be alphanumeric (no spaces, hyphens or slashes) and
 * should be globally unique per product. Suggested scheme:
 *   M{months}D{discount}    M1D30, M2D20, M3D40
 *   Put it anywhere in the plan name — end is easiest to read.
 *
 * ------------------------------------------------------------
 * DEBUGGING
 * ------------------------------------------------------------
 * Logging is OFF by default (this runs on every PDP). Enable with:
 *   - ?spdebug in the URL
 *   - localStorage.setItem('sp_debug', '1')
 *   - sp_debug: true on the Liquid snippet
 * Errors and ambiguity warnings always print.
 *
 * Inspect at runtime: window.SellingPlanLoader.getMapping()
 * Re-run after injecting new buy boxes: window.SellingPlanLoader.refresh()
 * ============================================================
 */

(function () {
  'use strict';

  var VERSION = '2.0.0';
  var CONFIG_SELECTOR = 'script.selling-plan-config, script[id="selling-plan-config"]';

  // The snippet renders once per buy box, so this file can be included
  // several times on one page. Later copies would otherwise replace
  // window.SellingPlanLoader with an empty-closure version. Bail early
  // and let the first instance own the page.
  if (window.SellingPlanLoader && window.SellingPlanLoader.version === VERSION) {
    return;
  }

  /* ----------------------------------------------------------
   * SMALL UTILITIES
   * ---------------------------------------------------------- */
  function toArray(nodeListOrArray) {
    return Array.prototype.slice.call(nodeListOrArray || []);
  }

  function unique(arr) {
    var out = [];
    arr.forEach(function (v) {
      if (v && out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  /** "1 Month (30% off)" → "1month30off" — strips ALL punctuation. */
  function compact(str) {
    if (str === null || str === undefined) return '';
    return String(str).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /** "1 Month Subscription M1D30" → ["1","month","subscription","m1d30"] */
  function tokenize(str) {
    if (str === null || str === undefined) return [];
    return String(str)
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
  }

  /** Legacy interval normalizer: "2 Months" → "2month" */
  function normalizeInterval(str) {
    return compact(str).replace(/s$/, '');
  }

  /** "M1D30|M1D20" → ["M1D30","M1D20"] */
  function splitCandidates(value) {
    if (value === null || value === undefined) return [];
    return String(value)
      .split(/[|,;]+/)
      .map(function (s) { return s.trim(); })
      .filter(Boolean);
  }

  /* ----------------------------------------------------------
   * CONFIG — merges EVERY config block on the page.
   *
   * The Liquid snippet is rendered once per buy box, so a page with
   * three buy boxes has three <script id="selling-plan-config">
   * blocks with duplicate IDs. getElementById only ever returned the
   * first one, so variants in the 2nd and 3rd buy boxes were silently
   * skipped. We merge them all instead.
   * ---------------------------------------------------------- */
  var variantConfig = {};
  var options = {};
  var DEBUG = false;

  // A token matching this is treated as a codeword, which both reserves
  // the plan carrying it and stops the candidate falling through to
  // fuzzy interval matching.
  var DEFAULT_CODE_PATTERN = /^[a-z]{1,4}\d+[a-z]{1,4}\d+$/i;
  var codePattern = DEFAULT_CODE_PATTERN;

  function loadConfigs() {
    var els = toArray(document.querySelectorAll(CONFIG_SELECTOR));
    var found = 0;

    els.forEach(function (el) {
      if (el.getAttribute('data-sp-parsed') === '1') return;
      var parsed;
      try {
        parsed = JSON.parse(el.textContent);
      } catch (e) {
        console.error('[SPLoader] Failed to parse a config block:', e, el);
        return;
      }
      el.setAttribute('data-sp-parsed', '1');
      found++;

      Object.keys(parsed.variants || {}).forEach(function (vid) {
        // First config to declare a variant wins; later duplicates are
        // ignored so two buy boxes sharing a variant stay consistent.
        if (!variantConfig[vid]) variantConfig[vid] = parsed.variants[vid];
      });

      Object.keys(parsed.options || {}).forEach(function (key) {
        if (!(key in options)) options[key] = parsed.options[key];
      });
    });

    DEBUG = !!options.debug ||
      /[?&]spdebug/i.test(window.location.search) ||
      (function () {
        try { return window.localStorage.getItem('sp_debug') === '1'; }
        catch (e) { return false; }
      })();

    if (options.codePattern) {
      try {
        codePattern = new RegExp(options.codePattern, 'i');
      } catch (e) {
        console.error('[SPLoader] Invalid codePattern "' + options.codePattern +
          '", using default.', e);
        codePattern = DEFAULT_CODE_PATTERN;
      }
    }

    return found;
  }

  function log() { if (DEBUG) console.log.apply(console, arguments); }
  function warn() { if (DEBUG) console.warn.apply(console, arguments); }
  function warnAlways() { console.warn.apply(console, arguments); }

  /* ----------------------------------------------------------
   * FETCH PRODUCT via AJAX API (.js endpoint)
   * ---------------------------------------------------------- */
  var productCache = {};

  function fetchProduct(handle) {
    if (!handle) return Promise.reject(new Error('No product handle'));
    if (productCache[handle]) return productCache[handle];

    var url = '/products/' + handle + '.js';
    log('[SPLoader] Fetching:', url);

    productCache[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) {
          if (res.status === 404) {
            throw new Error('404 on ' + url + ' — no product has the handle "' +
              handle + '". If the product was renamed, update sp_product_handle ' +
              'in the snippet call. No selling plans will resolve for it.');
          }
          throw new Error('Fetch failed: ' + url + ' (' + res.status + ')');
        }
        return res.json();
      })
      .then(function (product) {
        log('[SPLoader] Product "' + handle + '" loaded, ' +
          (product.variants ? product.variants.length : 0) + ' variants.');
        return product;
      });

    return productCache[handle];
  }

  /* ----------------------------------------------------------
   * BUILD SELLING PLAN LOOKUP
   *
   * For each plan we precompute:
   *   codeTokens    — every whole word/number in the name, option
   *                   values, description and group name. Used for
   *                   exact codeword matching.
   *   intervalForms — normalized name + normalized option values.
   *                   Used for legacy interval matching.
   * ---------------------------------------------------------- */
  function buildPlanLookup(product) {
    var lookup = {};

    (product.selling_plan_groups || []).forEach(function (group) {
      var groupTokens = tokenize(group.name);

      (group.selling_plans || []).forEach(function (plan) {
        var codeTokens = tokenize(plan.name).concat(groupTokens);
        var intervalForms = [normalizeInterval(plan.name)];

        (plan.options || []).forEach(function (opt) {
          codeTokens = codeTokens.concat(tokenize(opt.value));
          var form = normalizeInterval(opt.value);
          if (form) intervalForms.push(form);
        });

        if (plan.description) {
          codeTokens = codeTokens.concat(tokenize(plan.description));
        }

        codeTokens = unique(codeTokens);

        // Any token shaped like a codeword reserves this plan: legacy
        // interval matching will skip it so old metafields can't drift
        // onto a discount tier.
        var codes = codeTokens.filter(function (t) { return codePattern.test(t); });

        lookup[plan.id] = {
          id: plan.id,
          name: plan.name,
          codeTokens: codeTokens,
          codes: codes,
          reserved: codes.length > 0,
          intervalForms: unique(intervalForms)
        };
      });
    });

    return lookup;
  }

  /* ----------------------------------------------------------
   * EVALUATE ONE CANDIDATE AGAINST ALL ALLOCATIONS
   * Returns { planId, tier, ambiguous } or null.
   * ---------------------------------------------------------- */
  function evaluateCandidate(candidate, allocations, planLookup) {
    var raw = String(candidate).trim();

    // Accept a bare numeric ID or a gid://shopify/SellingPlan/123 URL.
    var idMatch = raw.match(/^(?:gid:\/\/shopify\/SellingPlan\/)?(\d{5,})$/i);
    var explicitId = idMatch ? idMatch[1] : null;

    var code = compact(raw);
    var interval = normalizeInterval(raw);

    // A candidate shaped like a codeword is matched strictly: exact
    // token or nothing. Letting "M1D3" substring its way into a plan
    // named "...M1D30" would be worse than failing.
    var candidateIsCode = code && codePattern.test(code);

    var hits = { id: [], code: [], exact: [], partial: [] };

    allocations.forEach(function (alloc) {
      var planId = alloc.selling_plan_id;
      var plan = planLookup[planId];

      if (explicitId && String(planId) === explicitId) {
        hits.id.push({ id: planId, reserved: false });
        return;
      }
      if (!plan) return;

      if (code && plan.codeTokens.indexOf(code) !== -1) {
        hits.code.push({ id: planId, reserved: plan.reserved });
        return;
      }

      if (candidateIsCode || !interval) return;

      if (plan.intervalForms.indexOf(interval) !== -1) {
        hits.exact.push({ id: planId, reserved: plan.reserved });
        return;
      }

      for (var i = 0; i < plan.intervalForms.length; i++) {
        var form = plan.intervalForms[i];
        if (form.indexOf(interval) !== -1 || interval.indexOf(form) !== -1) {
          hits.partial.push({ id: planId, reserved: plan.reserved });
          return;
        }
      }
    });

    /**
     * Drop reserved plans from a legacy interval tier. If that empties
     * the tier — every plan on the product is coded but this variant
     * still carries a legacy metafield — waive the rule rather than
     * resolve to nothing, and say so.
     */
    function applyReservation(list, tier) {
      var open = list.filter(function (h) { return !h.reserved; });

      if (open.length === list.length) return list;

      if (!open.length) {
        warnAlways('[SPLoader] "' + raw + '" only matched codeword-reserved ' +
          'plans at tier "' + tier + '". Waiving reservation and using ' +
          list[0].id + '. Move this variant onto a codeword to be sure ' +
          'it gets the tier you intend.');
        return list;
      }

      log('[SPLoader]   reservation dropped ' + (list.length - open.length) +
        ' coded plan(s) from tier "' + tier + '"');
      return open;
    }

    var ladder = [
      ['explicit-id', hits.id, false],
      ['code-token', hits.code, false],
      ['interval-exact', hits.exact, true],
      ['interval-partial', hits.partial, true]
    ];

    for (var t = 0; t < ladder.length; t++) {
      var tier = ladder[t][0];
      var list = ladder[t][1];
      if (!list.length) continue;

      if (ladder[t][2]) list = applyReservation(list, tier);

      if (list.length > 1) {
        warnAlways(
          '[SPLoader] AMBIGUOUS "' + raw + '" matched ' + list.length +
          ' plans at tier "' + tier + '": ' +
          list.map(function (h) { return h.id; }).join(', ') +
          '. Using ' + list[0].id + '. Add a unique codeword (e.g. M1D30) ' +
          'to each plan name and put it in the variant metafield.'
        );
      }
      return { planId: list[0].id, tier: tier, ambiguous: list.length > 1 };
    }

    return null;
  }

  /* ----------------------------------------------------------
   * MATCH SELLING PLAN FOR A VARIANT
   * ---------------------------------------------------------- */
  function matchPlan(variantId, metafieldValue, product) {
    var vid = parseInt(variantId, 10);
    var planLookup = buildPlanLookup(product);

    var variant = null;
    (product.variants || []).some(function (v) {
      if (v.id === vid) { variant = v; return true; }
      return false;
    });

    if (!variant) {
      warnAlways('[SPLoader] Variant ' + vid + ' not found in product "' + product.handle + '".');
      return null;
    }

    var allocations = variant.selling_plan_allocations || [];
    if (!allocations.length) {
      warnAlways('[SPLoader] No selling plan allocations for variant ' + vid +
        '. Check the plan is applied to this variant in Recharge/Shopify.');
      return null;
    }

    if (DEBUG) {
      log('[SPLoader] Variant ' + vid + ' (' + variant.title + ') metafield: "' +
        (metafieldValue || '') + '"');
      allocations.forEach(function (alloc) {
        var plan = planLookup[alloc.selling_plan_id];
        log('[SPLoader]   plan ' + alloc.selling_plan_id + ' → "' +
          (plan ? plan.name : 'UNKNOWN') + '"' +
          (plan && plan.reserved ? ' [RESERVED by code: ' + plan.codes.join(',') + ']' : '') +
          ' tokens: ' + (plan ? plan.codeTokens.join(',') : ''));
      });
    }

    var candidates = splitCandidates(metafieldValue);

    for (var i = 0; i < candidates.length; i++) {
      var hit = evaluateCandidate(candidates[i], allocations, planLookup);
      if (hit) {
        log('[SPLoader]   ✓ "' + candidates[i] + '" → plan ' + hit.planId +
          ' via ' + hit.tier);
        return hit.planId;
      }
      warn('[SPLoader]   ✗ no match for candidate "' + candidates[i] + '"');
    }

    // Fallback: only one plan available, nothing to get wrong.
    if (allocations.length === 1) {
      log('[SPLoader]   → fallback (single plan): ' + allocations[0].selling_plan_id);
      return allocations[0].selling_plan_id;
    }

    // Fallback: no metafield at all → first allocation. With discount
    // tiers this can hand out the wrong discount, so it is opt-out.
    if (!candidates.length) {
      if (options.fallbackToFirstPlan === false) {
        warnAlways('[SPLoader] Variant ' + vid + ' has no metafield and ' +
          allocations.length + ' plans; fallbackToFirstPlan is off, skipping.');
        return null;
      }
      warnAlways('[SPLoader] Variant ' + vid + ' has no metafield, falling back to ' +
        'first of ' + allocations.length + ' plans (' + allocations[0].selling_plan_id + ').');
      return allocations[0].selling_plan_id;
    }

    warnAlways('[SPLoader] No plan matched variant ' + vid + ' for "' +
      metafieldValue + '". Available: ' +
      allocations.map(function (a) {
        var p = planLookup[a.selling_plan_id];
        return a.selling_plan_id + ' "' + (p ? p.name : '?') + '"';
      }).join(' | '));
    return null;
  }

  /* ----------------------------------------------------------
   * POPULATE data-selling-plan ON BUY BOX INPUTS
   * ---------------------------------------------------------- */
  function populateBuyBox(mapping) {
    var subscribePanels = toArray(
      document.querySelectorAll('[id="product-selector-options-subscribe"]')
    );

    var radios;
    if (subscribePanels.length) {
      log('[SPLoader] Found ' + subscribePanels.length + ' subscribe panel(s).');
      radios = [];
      subscribePanels.forEach(function (panel) {
        radios = radios.concat(toArray(panel.querySelectorAll('input.product-selector-option_radio')));
      });
    } else {
      warn('[SPLoader] No subscribe panels found, searching whole document.');
      radios = toArray(document.querySelectorAll('input.product-selector-option_radio'));
    }

    var populated = 0;
    var unmatched = [];

    radios.forEach(function (radio) {
      var variantId = radio.getAttribute('value');
      if (!variantId || !mapping[variantId]) {
        if (variantId) unmatched.push(variantId);
        return;
      }

      var planId = mapping[variantId];
      radio.setAttribute('data-selling-plan', planId);

      // jQuery caches data-* attributes on first read. If it already
      // cached an empty value, .data('selling-plan') keeps returning ""
      // after setAttribute. Update both stores.
      if (typeof jQuery !== 'undefined') {
        jQuery(radio).data('selling-plan', planId);
        jQuery(radio).data('sellingPlan', planId);
      }

      populated++;
      log('[SPLoader] ✓ data-selling-plan="' + planId + '" on input[value="' + variantId + '"]');
    });

    // Zero matches with radios present almost always means the config
    // and the buy box disagree about variant IDs — a bad snippet call,
    // a stale ID, or a malformed config block. Say so unconditionally.
    if (radios.length && !populated) {
      warnAlways('[SPLoader] Found ' + radios.length + ' subscribe radio(s) but ' +
        'populated NONE. Radio values: [' + unique(unmatched).join(', ') + ']. ' +
        'Config variant IDs: [' + Object.keys(mapping).join(', ') + ']. ' +
        'If the config IDs look too long or run together, check the ' +
        'sp_variant_* arguments passed to the snippet.');
    } else if (!radios.length && Object.keys(mapping).length) {
      // Resolved plans with nowhere to put them. The buy box on this page
      // does not use input.product-selector-option_radio, so anything
      // reading the CTA href will never see a selling plan. Consumers
      // should fall back to SellingPlanLoader.getPlanForVariant().
      warnAlways('[SPLoader] Resolved ' + Object.keys(mapping).length + ' plan(s) but ' +
        'found NO input.product-selector-option_radio on the page. Nothing was ' +
        'written to the DOM. Mapping is still available via ' +
        'window.SellingPlanLoader.getPlanForVariant(variantId).');
    } else if (unmatched.length) {
      warn('[SPLoader] ' + unique(unmatched).length + ' radio(s) had no config entry: ' +
        unique(unmatched).join(', '));
    }

    log('[SPLoader] Populated ' + populated + ' of ' + radios.length + ' radio inputs.');
    return populated;
  }

  /* ----------------------------------------------------------
   * APPLY MAPPING
   * ---------------------------------------------------------- */
  var currentMapping = {};

  function applyMapping(mapping) {
    currentMapping = Object.assign({}, currentMapping, mapping);
    log('[SPLoader] ===== MAPPING =====', currentMapping);

    populateBuyBox(currentMapping);

    if (typeof initSubscribe === 'function') {
      initSubscribe();
      log('[SPLoader] ✓ Called initSubscribe() to rebuild CTA href.');
    }

    if (typeof jQuery !== 'undefined') {
      jQuery('[id="product-selector-options-subscribe"] input.product-selector-option_radio:checked')
        .trigger('change');
      log('[SPLoader] ✓ Triggered jQuery change on checked radios.');
    }

    document.dispatchEvent(
      new CustomEvent('selling-plans-loaded', { detail: { mapping: currentMapping } })
    );
  }

  /* ----------------------------------------------------------
   * INIT
   * ---------------------------------------------------------- */
  function init() {
    var configsFound = loadConfigs();
    if (!configsFound) return;

    var variantIds = Object.keys(variantConfig);
    if (!variantIds.length) {
      warnAlways('[SPLoader] Config found but it contains no variant IDs.');
      return;
    }

    log('[SPLoader] ===== INIT v' + VERSION + ' =====', variantIds);

    // Render-time validation from the Liquid snippet. A stale sp_product_handle
    // (product renamed) otherwise surfaces only as a 404 buried in the console,
    // with an empty mapping as the visible symptom.
    variantIds.forEach(function (vid) {
      var cfg = variantConfig[vid];
      if (cfg.handleResolved === false) {
        warnAlways('[SPLoader] Liquid could not find a product with handle "' +
          cfg.productHandle + '" (variant ' + vid + '). Most likely the product ' +
          'was renamed and sp_product_handle is stale — the fetch will 404 and ' +
          'no plan will resolve. Note this can also be a false alarm: all_products ' +
          'only exposes 20 products per page, so a busy page may report this ' +
          'for a handle that is actually fine.');
      } else if (cfg.variantFound === false) {
        warnAlways('[SPLoader] Handle "' + cfg.productHandle + '" resolves, but ' +
          'variant ' + vid + ' is not one of its variants. Check the sp_variant_* ' +
          'arguments — the variant may have been deleted or moved to another product.');
      }
    });

    // Anything Liquid already resolved server-side needs no fetch.
    var preResolved = {};
    var needsFetch = [];

    variantIds.forEach(function (vid) {
      var planId = variantConfig[vid].sellingPlanId;
      if (planId) {
        preResolved[vid] = String(planId);
        log('[SPLoader] Pre-resolved server-side: ' + vid + ' → ' + planId +
          ' ("' + (variantConfig[vid].sellingPlanName || '') + '")');
      } else {
        needsFetch.push(vid);
      }
    });

    if (!needsFetch.length) {
      log('[SPLoader] All plans resolved server-side — no fetch, no flash.');
      applyMapping(preResolved);
      return;
    }

    var handleGroups = {};
    needsFetch.forEach(function (vid) {
      var handle = variantConfig[vid].productHandle;
      if (!handle) {
        console.error('[SPLoader] No product handle for variant ' + vid +
          '. Pass sp_product_handle to the snippet.');
        return;
      }
      if (!handleGroups[handle]) handleGroups[handle] = [];
      handleGroups[handle].push(vid);
    });

    var uniqueHandles = Object.keys(handleGroups);
    if (!uniqueHandles.length) {
      applyMapping(preResolved);
      return;
    }

    Promise.all(
      uniqueHandles.map(function (handle) {
        return fetchProduct(handle)
          .then(function (product) { return { handle: handle, product: product }; })
          .catch(function (err) {
            console.error('[SPLoader] ' + handle + ':', err.message);
            return null;
          });
      })
    )
      .then(function (results) {
        var mapping = Object.assign({}, preResolved);

        results.forEach(function (result) {
          if (!result) return;
          handleGroups[result.handle].forEach(function (vid) {
            var planId = matchPlan(vid, variantConfig[vid].metafield, result.product);
            if (planId) mapping[vid] = String(planId);
          });
        });

        applyMapping(mapping);
      })
      .catch(function (err) {
        console.error('[SPLoader] Fatal:', err);
        if (Object.keys(preResolved).length) applyMapping(preResolved);
      });
  }

  /* ----------------------------------------------------------
   * PUBLIC API
   * ---------------------------------------------------------- */
  window.SellingPlanLoader = {
    version: VERSION,
    refresh: init,
    getMapping: function () { return Object.assign({}, currentMapping); },
    getPlanForVariant: function (variantId) { return currentMapping[String(variantId)] || null; },
    getConfig: function () { return variantConfig; },

    /**
     * Run the full match ladder against an already-fetched product payload.
     * For pages that have no buy box config block but still need the correct
     * plan — collection grids, quick-add tiles, upsell widgets.
     *
     *   product         parsed /products/{handle}.js response
     *   variantId       the variant to match for
     *   metafieldValue  that variant's subscription.delivery_interval,
     *                   passed down from Liquid (the AJAX API omits metafields)
     *
     * Returns a selling plan id, or null when nothing matches.
     */
    matchPlanForVariant: function (product, variantId, metafieldValue) {
      try {
        loadConfigs(); // picks up options.codePattern if a config exists
        return matchPlan(variantId, metafieldValue, product);
      } catch (e) {
        console.error('[SPLoader] matchPlanForVariant failed:', e);
        return null;
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();